import {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import apiService from '../services/apiService';
import { COLUMN_LAYOUT_KEY, DEFAULT_COLUMN_LAYOUT, normalizeColumnLayout } from '../utils/inventoryColumns';
import {
    PRESET_CONFLICT_MESSAGE, apiMessage, copyName, effectivePresetId, isNameConflict, presetLayout, validatePresetDraft,
} from '../utils/rolePresets';

/**
 * The admin "Role presets" section's state and calls (one dealership,
 * docs/decisions/role-presets.md): the presets with their counts, the
 * dealership's members (with their assignment and pick), the selected
 * preset and an editable draft { name, description, layout }.
 *
 * Writes are version-checked (expected_version); a 409 on the version sets
 * `conflict` ("Someone else changed this preset", Reload), a 409 about the
 * name marks the name field. Every write reloads the list (counts change).
 */

export const NEW_ID = '__new__';

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const draftOf = (preset) => (preset ? {
    name: preset.name || '',
    description: preset.description || '',
    layout: presetLayout(preset),
} : { name: '', description: '', layout: normalizeColumnLayout(DEFAULT_COLUMN_LAYOUT) });

/**
 * `onUsersChanged`: called after a write that changes what the admin user
 * list shows (an assignment, a rename, a delete), so the Users / Team data
 * is refreshed too (fresh QA F-2).
 */
export default function useRolePresetsAdmin(orgId, { onUsersChanged } = {}) {
    const [items, setItems] = useState([]);
    const [canEdit, setCanEdit] = useState(false);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [draft, setDraft] = useState(() => draftOf(null));
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [conflict, setConflict] = useState(false);
    const [busy, setBusy] = useState(false);
    const [history, setHistory] = useState(null);
    const seq = useRef(0);
    const usersChangedRef = useRef(onUsersChanged);
    usersChangedRef.current = onUsersChanged;
    const usersChanged = useCallback(() => {
        if (typeof usersChangedRef.current === 'function') usersChangedRef.current();
    }, []);

    const selected = useMemo(
        () => (selectedId && selectedId !== NEW_ID ? items.find((p) => p.id === selectedId) || null : null),
        [items, selectedId],
    );
    const isNew = selectedId === NEW_ID;
    const serverDraft = useMemo(() => draftOf(selected), [selected]);

    /** Load presets and members; keep (or pick) the selection. */
    const load = useCallback(async ({ select, keepDraft = false } = {}) => {
        if (!orgId) return;
        seq.current += 1;
        const mine = seq.current;
        setLoading(true);
        setLoadError(null);
        try {
            const [data, users] = await Promise.all([
                apiService.getAdminPresets(orgId),
                apiService.getAdminUsers(orgId).catch(() => null),
            ]);
            if (mine !== seq.current) return;
            const list = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
            setItems(list);
            setCanEdit(Boolean(data?.can_edit));
            if (Array.isArray(users)) setMembers(users.filter((u) => !u.org_id || u.org_id === orgId));
            setSelectedId((prev) => {
                const want = select !== undefined ? select : prev;
                const next = want === NEW_ID || list.some((p) => p.id === want) ? want : (list[0]?.id || null);
                const target = next === NEW_ID ? null : list.find((p) => p.id === next) || null;
                // A reload under the same preset (counts after an assignment) keeps unsaved edits.
                if (next !== NEW_ID && !(keepDraft && next === prev)) setDraft(draftOf(target));
                return next;
            });
            setConflict(false);
            if (!keepDraft) {
                setSaveError(null);
                setFieldErrors({});
            }
            setLoaded(true);
        } catch (err) {
            if (mine !== seq.current) return;
            setLoadError(apiMessage(err, 'Could not load the role presets.'));
        } finally {
            if (mine === seq.current) setLoading(false);
        }
    }, [orgId]);

    useEffect(() => {
        setItems([]);
        setMembers([]);
        setLoaded(false);
        setSelectedId(null);
        setHistory(null);
        load();
    }, [load]);

    const dirty = isNew || !same(draft, serverDraft);
    const others = useMemo(() => items.filter((p) => p.id !== selectedId), [items, selectedId]);
    const errors = useMemo(() => ({ ...validatePresetDraft(draft, others), ...fieldErrors }), [draft, others, fieldErrors]);
    const valid = Object.keys(validatePresetDraft(draft, others)).length === 0;

    const clearMessages = () => {
        setSaveError(null);
        setFieldErrors({});
    };

    /** Show another preset (or a blank one for "New"); the caller guards unsaved edits. */
    const select = useCallback((id) => {
        setSelectedId(id);
        setHistory(null);
        setConflict(false);
        setSaveError(null);
        setFieldErrors({});
        setDraft(id === NEW_ID ? draftOf(null) : draftOf(items.find((p) => p.id === id) || null));
    }, [items]);

    const setField = useCallback((key, value) => {
        setFieldErrors((prev) => {
            if (!(key in prev)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
        setDraft((prev) => ({ ...prev, [key]: value }));
    }, []);

    const discard = useCallback(() => {
        clearMessages();
        if (conflict) return load();
        if (isNew) {
            select(items[0]?.id || null);
            return Promise.resolve();
        }
        setDraft(serverDraft);
        return Promise.resolve();
    }, [conflict, load, isNew, items, select, serverDraft]);

    const failWrite = (err, fallback) => {
        if (err?.status === 409 && isNameConflict(err)) {
            setFieldErrors({ name: 'Another preset already has this name.' });
            setSaveError('Choose another name.');
        } else if (err?.status === 409) {
            setConflict(true);
            setSaveError(PRESET_CONFLICT_MESSAGE);
        } else if (err?.status === 422) {
            // { message, errors: { name, description, <setting key>: msg } }: mark the fields we show.
            const errs = err?.details?.detail?.errors;
            if (errs && typeof errs === 'object') {
                const marked = {};
                ['name', 'description'].forEach((k) => { if (errs[k]) marked[k] = [].concat(errs[k]).join(' '); });
                if (Object.keys(marked).length) setFieldErrors(marked);
            }
            setSaveError(apiMessage(err, 'The preset was not accepted.'));
        } else if (err?.status === 403) {
            setSaveError('You may not change the role presets of this dealership.');
        } else {
            setSaveError(apiMessage(err, fallback));
        }
    };

    /** Save the draft (create when new). Resolves to the saved preset or null. */
    const save = useCallback(async () => {
        if (!orgId || saving || !valid || !dirty) return null;
        const body = {
            name: draft.name.trim(),
            description: draft.description.trim(),
            values: { [COLUMN_LAYOUT_KEY]: normalizeColumnLayout(draft.layout) },
        };
        setSaving(true);
        clearMessages();
        setConflict(false);
        try {
            const saved = isNew
                ? await apiService.createAdminPreset(orgId, body)
                : await apiService.updateAdminPreset(orgId, selected.id, body, selected.version);
            await load({ select: saved?.id || (isNew ? undefined : selected.id) });
            if (!isNew && body.name !== selected.name) usersChanged();
            return saved || {};
        } catch (err) {
            failWrite(err, 'Could not save the preset. Please try again.');
            return null;
        } finally {
            setSaving(false);
        }
    }, [orgId, saving, valid, dirty, draft, isNew, selected, load, usersChanged]);

    const duplicate = useCallback(async () => {
        if (!selected || busy) return null;
        setBusy(true);
        clearMessages();
        try {
            const created = await apiService.createAdminPreset(orgId, {
                name: copyName(selected.name, items),
                description: selected.description || '',
                copy_from: selected.id,
            });
            await load({ select: created?.id });
            return created || {};
        } catch (err) {
            failWrite(err, 'Could not duplicate the preset.');
            return null;
        } finally {
            setBusy(false);
        }
    }, [selected, busy, orgId, items, load]);

    /**
     * Delete the selected preset, then show `nextId` (the first preset when
     * absent); resolves to { cleared_assignments, cleared_choices } or null.
     */
    const remove = useCallback(async (nextId = null) => {
        if (!selected || busy) return null;
        setBusy(true);
        clearMessages();
        try {
            const res = await apiService.deleteAdminPreset(orgId, selected.id, selected.version);
            await load({ select: nextId });
            usersChanged();
            return res || {};
        } catch (err) {
            failWrite(err, 'Could not delete the preset.');
            return null;
        } finally {
            setBusy(false);
        }
    }, [selected, busy, orgId, load, usersChanged]);

    /** Re-create deleted seeded presets; resolves to how many came back (null on failure). */
    const restoreDefaults = useCallback(async () => {
        if (busy) return null;
        setBusy(true);
        clearMessages();
        try {
            const res = await apiService.restoreAdminDefaultPresets(orgId);
            const list = Array.isArray(res) ? res : (Array.isArray(res?.items) ? res.items : []);
            await load({ keepDraft: true });
            return list.length;
        } catch (err) {
            failWrite(err, 'Could not restore the default presets.');
            return null;
        } finally {
            setBusy(false);
        }
    }, [busy, orgId, load]);

    const loadHistory = useCallback(async () => {
        if (!selected) return;
        setHistory({ loading: true, rows: [] });
        try {
            const rows = await apiService.getAdminPresetHistory(orgId, selected.id, 20);
            setHistory({ loading: false, rows: Array.isArray(rows) ? rows : [] });
        } catch (err) {
            setHistory({ loading: false, rows: [], error: apiMessage(err, 'Could not load the history.') });
        }
    }, [orgId, selected]);

    /**
     * Set the admin's assignment of these members to the selected preset:
     * `add` get it, `drop` go back to none (system default). One PUT each.
     */
    const assign = useCallback(async ({ add = [], drop = [] }) => {
        if (!selected || busy) return false;
        setBusy(true);
        clearMessages();
        try {
            // eslint-disable-next-line no-restricted-syntax
            for (const id of add) await apiService.setAdminMemberPreset(orgId, id, selected.id); // eslint-disable-line no-await-in-loop
            // eslint-disable-next-line no-restricted-syntax
            for (const id of drop) await apiService.setAdminMemberPreset(orgId, id, null); // eslint-disable-line no-await-in-loop
            await load({ keepDraft: true });
            return true;
        } catch (err) {
            setSaveError(apiMessage(err, 'Could not change the assignments.'));
            await load({ keepDraft: true });
            return false;
        } finally {
            // Some writes may have landed even when one failed.
            usersChanged();
            setBusy(false);
        }
    }, [selected, busy, orgId, load, usersChanged]);

    const membersOf = useCallback(
        (presetId) => members.filter((u) => effectivePresetId(u, items) === presetId),
        [members, items],
    );

    return {
        loading,
        loaded,
        loadError,
        items,
        canEdit,
        members,
        membersOf,
        selectedId,
        selected,
        isNew,
        draft,
        errors,
        valid,
        dirty,
        saving,
        saveError,
        conflict,
        busy,
        history,
        select,
        setField,
        discard,
        save,
        duplicate,
        remove,
        restoreDefaults,
        loadHistory,
        assign,
        reload: load,
    };
}
